// Directory tabs are real paths (/experts, /companies, /pr_profiles, /products, /story_ideas,
// /media_profiles, /media_outlets), not URL fragments — the frontend engine may be mounted at a
// prefix, so tab identity and tab links are derived from the pathname at call time rather than
// hardcoded.

export const DEFAULT_TAB = "experts";
export const VALID_TABS = [
  "experts",
  "products",
  "pr_profiles",
  "companies",
  "story_ideas",
  "media_profiles",
  "media_outlets",
];

// V1-COMPAT: optional map of V2 tab key -> host-supplied absolute path (e.g. {experts: "/sources"}),
// so a host page (like vested-rails) can serve these tabs at its own paths. Both tabFromPathname and
// tabPath consult it first when present; with no map, behavior is unchanged.
function tabFromMappedPath(lastSegment, tabPathMap) {
  const mappedTab = Object.keys(tabPathMap).find((tab) => {
    const mappedSegments = tabPathMap[tab].split("/").filter(Boolean);
    return mappedSegments[mappedSegments.length - 1] === lastSegment;
  });
  return VALID_TABS.includes(mappedTab) ? mappedTab : undefined;
}

// Matches the last path segment against the valid tab set, falling back to the server-passed
// active tab (from the initial page load) and finally to the default tab.
export function tabFromPathname(pathname, fallbackTab, tabPathMap) {
  const segments = pathname.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1];
  if (tabPathMap) {
    const mappedTab = tabFromMappedPath(lastSegment, tabPathMap);
    if (mappedTab) return mappedTab;
  }
  if (VALID_TABS.includes(lastSegment)) return lastSegment;
  if (VALID_TABS.includes(fallbackTab)) return fallbackTab;
  return DEFAULT_TAB;
}

// The path prefix the frontend engine is mounted under, derived from the current pathname by
// dropping its last segment (the current tab). "" at the root, e.g. "/app" if mounted deeper.
function directoryBasePath(pathname) {
  const segments = pathname.split("/").filter(Boolean);
  segments.pop();
  return segments.length ? `/${segments.join("/")}` : "";
}

export function tabPath(pathname, tabKey, tabPathMap) {
  // V1-COMPAT: a mapped path is the host's own absolute route, used verbatim rather than grafted
  // onto this app's mount prefix.
  if (tabPathMap && tabPathMap[tabKey]) return tabPathMap[tabKey];
  return `${directoryBasePath(pathname)}/${tabKey}`;
}

// react-rails's camelize_props deep-camelizes prop keys, so the server's tab_titles hash
// arrives with "pr_profiles" turned into "prProfiles". Look up both spellings.
export function tabTitle(tabTitles, tab) {
  if (!tabTitles) return undefined;
  return tabTitles[tab] ?? tabTitles[tab.replace(/_(\w)/g, (match, chr) => chr.toUpperCase())];
}

export function applyTabTitle(tabTitles, tab) {
  const title = tabTitle(tabTitles, tab);
  if (title) document.title = `${title} - Qwoted`;
}
